import React, { useState } from "react";
import * as resellerService from "@/shared/api/services/resellerService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { LanguageProvider } from "@/shared/components/shared/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Plus, Search, MoreHorizontal, Pencil, Power, PowerOff,
  Building2, CheckCircle2, XCircle, Clock, RefreshCw,
  Users, TrendingUp, ShieldCheck, Zap,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import ResellerMerchantForm from "@/shared/components/reseller/ResellerMerchantForm";

const statusConfig = {
  ACTIVE:    { icon:CheckCircle2, color:"#10b981", bg:"rgba(16,185,129,.12)", border:"rgba(16,185,129,.28)", label:"Active"    },
  active:    { icon:CheckCircle2, color:"#10b981", bg:"rgba(16,185,129,.12)", border:"rgba(16,185,129,.28)", label:"Active"    },
  SUSPENDED: { icon:XCircle,      color:"#ef4444", bg:"rgba(239,68,68,.12)",  border:"rgba(239,68,68,.28)",  label:"Suspended" },
  suspended: { icon:XCircle,      color:"#ef4444", bg:"rgba(239,68,68,.12)",  border:"rgba(239,68,68,.28)",  label:"Suspended" },
  INACTIVE:  { icon:Clock,        color:"#f59e0b", bg:"rgba(245,158,11,.12)", border:"rgba(245,158,11,.28)", label:"Inactive"  },
  pending:   { icon:Clock,        color:"#f59e0b", bg:"rgba(245,158,11,.12)", border:"rgba(245,158,11,.28)", label:"Pending"   },
};

/* ── Premium Stat Card ── */
function StatCard({ label, value, icon: Icon, color, delay }) {
  const [h, setH] = useState(false);
  return (
    <motion.div
      initial={{ opacity:0, y:24 }}
      animate={{ opacity:1, y:0 }}
      transition={{ delay, duration:.48, ease:[0.34,1.56,0.64,1] }}
      onMouseEnter={()=>setH(true)}
      onMouseLeave={()=>setH(false)}
      style={{
        background: "linear-gradient(135deg,rgba(13,27,46,.88) 0%,rgba(15,30,55,.88) 100%)",
        border: `1px solid ${h ? color+"55" : color+"22"}`,
        borderRadius:"18px",
        padding:"22px",
        position:"relative",
        overflow:"hidden",
        transition:"all .48s cubic-bezier(0.34,1.56,0.64,1)",
        transform: h ? "translateY(-9px) scale(1.03)" : "translateY(0) scale(1)",
        boxShadow: h ? `0 22px 52px ${color}30, 0 0 0 1px ${color}25` : "0 2px 12px rgba(0,0,0,.28)",
        cursor:"default",
      }}
    >
      {/* radial glow */}
      <div style={{ position:"absolute", inset:0, background: h ? `radial-gradient(ellipse at 85% 15%, ${color}20 0%, transparent 60%)` : "none", pointerEvents:"none", transition:"all .45s ease" }} />
      {/* animated top gradient line */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px", background: h ? `linear-gradient(90deg,transparent,${color}95,${color}80,transparent)` : `linear-gradient(90deg,transparent,${color}30,transparent)`, transition:"all .45s ease", borderRadius:"18px 18px 0 0" }} />
      {/* animated bottom line */}
      {h && <div style={{ position:"absolute", bottom:0, left:"15%", right:"15%", height:"1px", background:`linear-gradient(90deg,transparent,${color}60,transparent)`, animation:"accentLineGrow .4s ease forwards" }} />}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"14px" }}>
        <p style={{ fontSize:"10px", color:"#64748b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em" }}>{label}</p>
        {/* icon with tilt + scale micro-interaction */}
        <div style={{
          width:"38px", height:"38px",
          borderRadius:"11px",
          background: h ? `linear-gradient(135deg,${color}30,${color}20)` : `${color}18`,
          border: `1px solid ${h ? color+"45" : color+"28"}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)",
          transform: h ? "rotate(-8deg) scale(1.18)" : "rotate(0) scale(1)",
          boxShadow: h ? `0 4px 14px ${color}35` : "none",
        }}>
          <Icon style={{ width:"17px", height:"17px", color, transition:"color .2s" }} />
        </div>
      </div>
      <p style={{ fontSize:"26px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.03em", lineHeight:1 }}>{value}</p>
    </motion.div>
  );
}

/* ── Status badge ── */
function SBadge({ status }) {
  const c = statusConfig[status] || statusConfig.pending;
  const Icon = c.icon;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", background:c.bg, border:`1px solid ${c.border}`, fontSize:"11px", fontWeight:600, color:c.color }}>
      <Icon style={{ width:"11px", height:"11px" }} />{c.label}
    </span>
  );
}

/* ── Merchant Table Row ── */
function MerchantRow({ m, i, language, t, onEdit, onSuspend }) {
  const [h, setH] = useState(false);
  return (
    <motion.tr
      initial={{ opacity:0, x:-10 }}
      animate={{ opacity:1, x:0 }}
      transition={{ delay: i * 0.03, duration:.4, ease:[0.34,1.56,0.64,1] }}
      onMouseEnter={()=>setH(true)}
      onMouseLeave={()=>setH(false)}
      style={{
        borderBottom:"1px solid rgba(99,102,241,.05)",
        background: h ? "rgba(99,102,241,.05)" : "transparent",
        transition:"background .22s ease",
      }}
    >
      <td style={{ padding:"15px 18px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"13px" }}>
          {/* icon with tilt micro-interaction */}
          <div style={{
            width:"42px", height:"42px",
            borderRadius:"12px",
            background: h ? "linear-gradient(135deg,rgba(99,102,241,.28),rgba(139,92,246,.22))" : "linear-gradient(135deg,rgba(99,102,241,.18),rgba(139,92,246,.12))",
            border: `1px solid ${h ? "rgba(99,102,241,.35)" : "rgba(99,102,241,.2)"}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            flexShrink:0,
            transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)",
            transform: h ? "rotate(-6deg) scale(1.1)" : "rotate(0) scale(1)",
            boxShadow: h ? "0 4px 16px rgba(99,102,241,.3)" : "none",
          }}>
            <Building2 style={{ width:"18px", height:"18px", color: h ? "#a5b4fc" : "#818cf8", transition:"color .2s" }} />
          </div>
          <div>
            <p style={{ margin:0, fontSize:"13px", fontWeight:700, color: h ? "#f0f9ff" : "#e2e8f0", transition:"color .2s" }}>{m.name}</p>
            <p style={{ margin:0, fontSize:"11px", color:"#475569" }}>{m.email || ""}</p>
          </div>
        </div>
      </td>
      <td style={{ padding:"15px 18px" }}>
        <span style={{ fontFamily:"monospace", fontSize:"11px", color:"#818cf8", background:"rgba(99,102,241,.1)", padding:"4px 9px", borderRadius:"7px", border:"1px solid rgba(99,102,241,.22)", fontWeight:700, letterSpacing:"0.04em" }}>
          {m.code}
        </span>
      </td>
      <td style={{ padding:"15px 18px" }}><SBadge status={m.status} /></td>
      <td style={{ padding:"15px 18px", textAlign:"right" }}>
        <span style={{ fontSize:"15px", fontWeight:800, color: h ? "#a5b4fc" : "#f0f9ff", transition:"color .2s" }}>฿0</span>
      </td>
      <td style={{ padding:"15px 18px" }}>
        <span style={{ fontSize:"12px", color:"#64748b" }}>
          {m.created_date ? format(new Date(m.created_date), language==="th"?"d MMM yyyy":"MMM d, yyyy", { locale:language==="th"?th:enUS }) : "N/A"}
        </span>
      </td>
      <td style={{ padding:"15px 18px" }}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              style={{ width:"34px", height:"34px", borderRadius:"9px", background: h?"rgba(99,102,241,.18)":"rgba(148,163,184,.06)", border:`1px solid ${h?"rgba(99,102,241,.3)":"rgba(148,163,184,.1)"}`, color: h?"#a5b4fc":"#64748b", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all .3s cubic-bezier(0.34,1.56,0.64,1)", transform: h?"scale(1.1)":"scale(1)" }}
            >
              <MoreHorizontal style={{ width:"15px", height:"15px" }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" style={{ background:"#0d1b2e", border:"1px solid rgba(99,102,241,.2)" }}>
            <DropdownMenuItem onClick={()=>onEdit(m)}>
              <Pencil style={{ width:"14px", height:"14px", marginRight:"8px", color:"#818cf8" }} />
              <span style={{ color:"#e2e8f0" }}>{t("edit")||"Edit"}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={()=>onSuspend(m)}>
              {["ACTIVE","active"].includes(m.status)
                ? <><PowerOff style={{ width:"14px", height:"14px", marginRight:"8px", color:"#ef4444" }} /><span style={{ color:"#ef4444" }}>{t("suspend")||"Suspend"}</span></>
                : <><Power style={{ width:"14px", height:"14px", marginRight:"8px", color:"#10b981" }} /><span style={{ color:"#10b981" }}>{t("activate")||"Activate"}</span></>
              }
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </motion.tr>
  );
}

function ResellerMerchantsContent() {
  const [search, setSearch]           = useState("");
  const [suspendDialog, setSuspend]   = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [editingMerchant, setEditing] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [sf, setSf]                   = useState(false);
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const resellerName = "Asia Gaming Network";
  const resellerCode = "RSL001";

  const { data: merchants = [], isLoading } = useQuery({
    queryKey: ["reseller-merchants"],
    queryFn: () => resellerService.getMyMerchants(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => resellerService.updateMerchant(id, data),
    onSuccess: () => { queryClient.invalidateQueries(["reseller-merchants"]); setSuspend(null); },
  });

  const createMutation = useMutation({
    mutationFn: (data) => resellerService.createMerchant(data),
    onSuccess: () => { queryClient.invalidateQueries(["reseller-merchants"]); setShowForm(false); setEditing(null); setSaving(false); },
  });

  const handleSaveMerchant = async (data) => {
    setSaving(true);
    try {
      if (editingMerchant) {
        await updateMutation.mutateAsync({ id: editingMerchant.id, data });
      } else {
        const createData = { email:data.email, password:data.password, name:data.name, apiKey:data.apiKey||undefined, secretKey:data.secretKey||undefined, webhook:data.webhook||undefined, callback:data.callback||undefined };
        await createMutation.mutateAsync(createData);
      }
    } catch {}
    finally { setSaving(false); setShowForm(false); setEditing(null); }
  };

  const handleToggleStatus = async () => {
    if (!suspendDialog) return;
    const cur = suspendDialog.status?.toUpperCase();
    const newStatus = cur === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id:suspendDialog.id, data:{ name:suspendDialog.name, status:newStatus } });
  };

  const filteredMerchants = merchants.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.code?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount    = merchants.filter(m=>["ACTIVE","active"].includes(m.status)).length;
  const suspendedCount = merchants.filter(m=>["SUSPENDED","suspended"].includes(m.status)).length;

  return (
    <ResellerLayout currentPage="ResellerMerchants" resellerName={resellerName} resellerCode={resellerCode}>
      <style>{`
        @keyframes rspin3{to{transform:rotate(360deg)}}
        @keyframes accentLineGrow{from{opacity:0;transform:scaleX(0)}to{opacity:1;transform:scaleX(1)}}
      `}</style>
      <div style={{ maxWidth:"1400px" }}>

        {/* ── Header ── */}
        <motion.div initial={{ opacity:0, y:-14 }} animate={{ opacity:1, y:0 }} transition={{ duration:.42, ease:[0.34,1.56,0.64,1] }} style={{ marginBottom:"28px" }}>
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between", gap:"16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
              <div style={{ width:"46px", height:"46px", borderRadius:"14px", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(99,102,241,.5)", flexShrink:0 }}>
                <Building2 style={{ width:"21px", height:"21px", color:"#fff" }} />
              </div>
              <div>
                <h1 style={{ fontSize:"24px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.025em", margin:0 }}>{t("merchants")||"Merchants"}</h1>
                <p style={{ fontSize:"13px", color:"#475569", margin:0 }}>{merchants.length} merchants registered</p>
              </div>
            </div>
            {/* Add Merchant button with elastic lift */}
            <button
              onClick={()=>{ setEditing(null); setShowForm(true); }}
              style={{ display:"flex", alignItems:"center", gap:"8px", padding:"11px 22px", borderRadius:"13px", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", border:"none", color:"#fff", fontSize:"13px", fontWeight:700, cursor:"pointer", transition:"all .45s cubic-bezier(0.34,1.56,0.64,1)", boxShadow:"0 4px 18px rgba(99,102,241,.5)", letterSpacing:"0.01em" }}
              onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-4px) scale(1.05)"; e.currentTarget.style.boxShadow="0 10px 30px rgba(99,102,241,.65)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform="translateY(0) scale(1)"; e.currentTarget.style.boxShadow="0 4px 18px rgba(99,102,241,.5)"; }}
            >
              <Plus style={{ width:"16px", height:"16px" }} />{t("addMerchant")||"Add Merchant"}
            </button>
          </div>
        </motion.div>

        {/* ── Stat cards ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(155px,1fr))", gap:"14px", marginBottom:"26px" }}>
          <StatCard label="Total"      value={merchants.length} icon={Users}        color="#6366f1" delay={0}    />
          <StatCard label="Active"     value={activeCount}      icon={CheckCircle2} color="#10b981" delay={0.06} />
          <StatCard label="Suspended"  value={suspendedCount}   icon={XCircle}      color="#ef4444" delay={0.12} />
          <StatCard label="Today Vol." value="฿0"               icon={TrendingUp}   color="#38bdf8" delay={0.18} />
        </div>

        {/* ── Search ── */}
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.2 }} style={{ marginBottom:"18px", maxWidth:"400px" }}>
          <div style={{ position:"relative" }}>
            <Search style={{ position:"absolute", left:"14px", top:"50%", transform:"translateY(-50%)", width:"15px", height:"15px", color: sf?"#6366f1":"#64748b", transition:"color .22s" }} />
            <input
              value={search} onChange={e=>setSearch(e.target.value)}
              onFocus={()=>setSf(true)} onBlur={()=>setSf(false)}
              placeholder={t("search")||"Search merchants..."}
              style={{ width:"100%", padding:"11px 14px 11px 42px", borderRadius:"13px", background:"rgba(13,27,46,.85)", border:`1px solid ${sf?"rgba(99,102,241,.5)":"rgba(99,102,241,.14)"}`, color:"#e2e8f0", fontSize:"13px", outline:"none", transition:"all .32s ease", boxShadow: sf?"0 0 0 3px rgba(99,102,241,.12)":"none" }}
            />
          </div>
        </motion.div>

        {/* ── Table ── */}
        <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:.26, ease:[0.34,1.56,0.64,1] }}
          style={{ background:"linear-gradient(135deg,rgba(13,27,46,.93) 0%,rgba(15,30,55,.93) 100%)", border:"1px solid rgba(99,102,241,.14)", borderRadius:"22px", overflow:"hidden" }}>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid rgba(99,102,241,.1)", background:"rgba(8,14,26,.65)" }}>
                  {[t("merchant")||"Merchant", t("code")||"Code", t("status")||"Status", t("today")||"Today", t("created")||"Created", ""].map((h,i)=>(
                    <th key={i} style={{ padding:"14px 18px", textAlign: i===3?"right":"left", fontSize:"10px", fontWeight:700, color:"#334155", textTransform:"uppercase", letterSpacing:"0.1em", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} style={{ textAlign:"center", padding:"72px 24px", color:"#475569" }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"14px" }}>
                      <RefreshCw style={{ width:"30px", height:"30px", color:"#6366f1", animation:"rspin3 1s linear infinite" }} />
                      <span style={{ fontSize:"13px" }}>Loading merchants…</span>
                    </div>
                  </td></tr>
                ) : filteredMerchants.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign:"center", padding:"80px 24px", color:"#334155" }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"16px" }}>
                      <Building2 style={{ width:"56px", height:"56px", color:"#1e293b" }} />
                      <p style={{ margin:0, fontSize:"14px" }}>{t("noMerchantsFound")||"No merchants found"}</p>
                    </div>
                  </td></tr>
                ) : filteredMerchants.map((m, i) => (
                  <MerchantRow key={m.id} m={m} i={i} language={language} t={t}
                    onEdit={(m)=>{ setEditing(m); setShowForm(true); }}
                    onSuspend={setSuspend}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* ── Alert Dialog ── */}
        <AlertDialog open={!!suspendDialog} onOpenChange={()=>setSuspend(null)}>
          <AlertDialogContent style={{ background:"#0d1b2e", border:"1px solid rgba(99,102,241,.22)", borderRadius:"18px" }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color:"#f0f9ff" }}>
                {["ACTIVE","active"].includes(suspendDialog?.status) ? t("suspendMerchantTitle")||"Suspend Merchant" : t("activateMerchantTitle")||"Activate Merchant"}
              </AlertDialogTitle>
              <AlertDialogDescription style={{ color:"#64748b" }}>
                {["ACTIVE","active"].includes(suspendDialog?.status)
                  ? `${t("suspendMerchantConfirm")||"Suspend"} "${suspendDialog?.name}"? ${t("willNotProcessTransactions")||"They will not be able to process transactions."}`
                  : `${t("activateMerchantConfirm")||"Activate"} "${suspendDialog?.name}"?`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ background:"rgba(148,163,184,.1)", border:"1px solid rgba(148,163,184,.2)", color:"#94a3b8", borderRadius:"10px" }}>
                {t("cancel")||"Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleToggleStatus}
                style={{ background:["ACTIVE","active"].includes(suspendDialog?.status)?"#dc2626":"#059669", border:"none", borderRadius:"10px" }}>
                {["ACTIVE","active"].includes(suspendDialog?.status) ? t("suspend")||"Suspend" : t("activate")||"Activate"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ResellerMerchantForm
          open={showForm}
          onClose={()=>{ setShowForm(false); setEditing(null); }}
          merchant={editingMerchant}
          onSave={handleSaveMerchant}
          saving={saving}
        />
      </div>
    </ResellerLayout>
  );
}

export default function ResellerMerchants() {
  return (
    <LanguageProvider>
      <ResellerMerchantsContent />
    </LanguageProvider>
  );
}